import { useEffect } from "react";
import { ArrowLeftRight, ArrowRight, Repeat, Route } from "lucide-react";
import { BOOKING_TYPES, WAIT_OPTIONS, waitLabel } from "@/lib/rides";
import { Input } from "@/components/ui/input";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TYPE_META = {
  one_way: { icon: ArrowRight, sub: "A to B" },
  round_trip: { icon: ArrowLeftRight, sub: "There & back" },
  multi_stop: { icon: Route, sub: "Stops in order" },
  recurring: { icon: Repeat, sub: "Weekly schedule" },
};
const KINDS = { one_way: "One-way", round_trip: "Round trip", multi_stop: "Multi-stop" };
const Label = ({ children }) => <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>;

export const Chip = ({ active, onClick, children, testid, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-[background-color,box-shadow] disabled:cursor-not-allowed disabled:opacity-40 ${
      active ? "border-primary bg-primary text-primary-foreground shadow-[0_0_12px_hsl(var(--primary)/0.5)]" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>
    {children}
  </button>
);

export const toLocalInput = (d) => new Date(new Date(d).getTime() - new Date(d).getTimezoneOffset() * 60000).toISOString().slice(0, 16);
export const earliestLocal = (lead = 10) => {
  const d = new Date(Date.now() + lead * 60000);
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
  return toLocalInput(d);
};

export function BookingTypePicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="booking-type-picker">
      {Object.entries(BOOKING_TYPES).map(([k, label]) => {
        const { icon: Icon, sub } = TYPE_META[k];
        const on = value === k;
        return (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`booking-type-${k}`}
            className={`rounded-2xl border p-3 text-left transition-[border-color,box-shadow,background-color] ${on ? "border-primary bg-primary/10 shadow-[0_0_16px_hsl(var(--primary)/0.35)]" : "border-white/10 hover:border-white/30"}`}>
            <Icon className={`h-4 w-4 ${on ? "text-primary" : "text-muted-foreground"}`} />
            <p className="mt-1.5 text-sm font-semibold">{label}</p>
            <p className="text-[11px] text-muted-foreground">{sub}</p>
          </button>
        );
      })}
    </div>
  );
}

const WaitSelect = ({ value, onChange, tiers, testid }) => (
  <select value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}
    className="rounded-lg border bg-background px-2 py-1.5 text-sm text-foreground">
    {WAIT_OPTIONS.map((w) => <option key={w} value={w}>{waitLabel(w, tiers)}</option>)}
  </select>
);

export function RecurringOptions({ opts, set, config, hideKind }) {
  const r = config?.recurring || { min_legs_per_week: 7, end_of_week_pct: 20, prepaid_pct: 30, round_down_to: 5 };
  const toggleDay = (d) => set({ days: opts.days.includes(d) ? opts.days.filter((x) => x !== d) : [...opts.days, d] });
  return (
    <div className="space-y-3 border-t pt-3 first:border-t-0 first:pt-0" data-testid="recurring-options">
      {!hideKind && <div><Label>Each ride is</Label><div className="flex flex-wrap gap-2">
        {Object.entries(KINDS).map(([k, v]) => <Chip key={k} active={opts.recurringKind === k} onClick={() => set({ recurringKind: k })} testid={`recurring-kind-${k}`}>{v}</Chip>)}
      </div></div>}
      <div><Label>Days</Label><div className="flex flex-wrap gap-1.5">
        {DAYS.map((d, i) => <Chip key={d} active={opts.days.includes(i)} onClick={() => toggleDay(i)} testid={`recurring-day-${i}`}>{d}</Chip>)}
      </div></div>
      <label className="flex items-center justify-between text-xs text-muted-foreground">
        For how many weeks
        <select value={opts.weeks} onChange={(e) => set({ weeks: Number(e.target.value) })} data-testid="recurring-weeks-select"
          className="rounded-lg border bg-background px-2 py-1 text-sm text-foreground">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((w) => <option key={w} value={w}>{w} week{w > 1 ? "s" : ""}</option>)}
        </select>
      </label>
      <div><Label>Payment</Label><div className="flex flex-wrap gap-2">
        <Chip active={opts.payment === "end_of_week"} onClick={() => set({ payment: "end_of_week" })} testid="recurring-pay-end-of-week">Pay end of week · {r.end_of_week_pct}% off</Chip>
        <Chip active={opts.payment === "prepaid"} onClick={() => set({ payment: "prepaid" })} testid="recurring-pay-prepaid">Prepay week · {r.prepaid_pct}% off</Chip>
      </div></div>
      <p className="text-xs text-neon-lime" data-testid="recurring-rule-note">
        Discount applies to weeks with {r.min_legs_per_week}+ ride legs (a round trip counts as 2). Weekly total rounds down to the nearest ${r.round_down_to}.
      </p>
    </div>
  );
}

function WhenPicker({ opts, set, recurring, allowNow, sched }) {
  const lead = sched?.lead_minutes ?? 10;
  const ask = recurring || !allowNow || opts.when === "later";
  useEffect(() => {
    if (ask && !opts.scheduledAt && sched) set({ when: "later", scheduledAt: earliestLocal(lead) });
  }, [ask, opts.scheduledAt, sched]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <Label>{recurring ? "First ride" : "Pickup time"}</Label>
      <div className="flex flex-wrap items-center gap-2">
        {allowNow && !recurring && <Chip active={opts.when === "now"} onClick={() => set({ when: "now" })} testid="when-now">Now</Chip>}
        {allowNow && !recurring && <Chip active={opts.when === "later"} onClick={() => set({ when: "later" })} testid="when-later">Schedule</Chip>}
        {ask && (
          <Input type="datetime-local" min={allowNow ? undefined : earliestLocal(lead)} value={opts.scheduledAt} onChange={(e) => set({ scheduledAt: e.target.value })}
            className="h-9 w-auto flex-1 rounded-full [color-scheme:dark]" data-testid="schedule-datetime-input" />
        )}
      </div>
      {sched && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="schedule-rules-note">
          Book at least {sched.lead_minutes} minutes ahead. Scoopz keeps {sched.gap_minutes} minutes free after each booked ride.
        </p>
      )}
    </div>
  );
}

export function TripOptions({ opts, setOpts, config, allowNow = false, hideWhen = false }) {
  const set = (patch) => setOpts({ ...opts, ...patch });
  const recurring = opts.bookingType === "recurring";
  return (
    <div className="space-y-4 rounded-2xl border bg-card p-4" data-testid="trip-options">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label>Vehicle</Label>
          <div className="flex gap-2">
            {Object.entries(config?.vehicles || { xl: { label: "XL", available: true } }).map(([k, v]) => (
              <Chip key={k} active={opts.vehicle === k} disabled={!v.available} onClick={() => set({ vehicle: k })} testid={`vehicle-${k}`}>
                {v.label}{!v.available ? " · soon" : ""}
              </Chip>
            ))}
          </div>
        </div>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span className="block font-semibold uppercase tracking-wider">Passengers</span>
          <select value={opts.passengers || 1} onChange={(e) => set({ passengers: Number(e.target.value) })} data-testid="passengers-select"
            className="rounded-lg border bg-background px-2 py-1.5 text-sm text-foreground">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      {!hideWhen && <WhenPicker opts={opts} set={set} recurring={recurring} allowNow={allowNow} sched={config?.scheduling} />}
    </div>
  );
}

export function TypeOptions({ opts, setOpts, config, hideKind }) {
  const set = (patch) => setOpts({ ...opts, ...patch });
  const recurring = opts.bookingType === "recurring";
  const roundTrip = (recurring ? opts.recurringKind : opts.bookingType) === "round_trip";
  if (!roundTrip && !recurring) return null;
  return (
    <div className="space-y-4 rounded-2xl border bg-card p-4" data-testid="type-options">
      {roundTrip && (
        <label className="flex items-center justify-between gap-3 text-sm">
          <span><span className="block font-semibold">Wait at destination</span><span className="text-xs text-muted-foreground">Before the ride back to pickup</span></span>
          <WaitSelect value={opts.returnWait} onChange={(v) => set({ returnWait: v })} tiers={config?.wait_tiers} testid="return-wait-select" />
        </label>
      )}
      {recurring && <RecurringOptions opts={opts} set={set} config={config} hideKind={hideKind} />}
    </div>
  );
}

export { WaitSelect };
